import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogSurface,
    DialogBody,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Text,
    Card,
    Badge,
    Textarea,
    Tab,
    TabList,
    Field,
    Input,
    Divider,
    ProgressBar,
    Toast,
    ToastTitle,
    Toaster,
    useToastController,
} from '@fluentui/react-components';
import {
    Document24Regular,
    Edit24Regular,
    Save24Regular,
    Send24Regular,
    Warning24Regular,
    CheckmarkCircle24Regular,
    Dismiss24Regular,
    Bot24Regular,
    Person24Regular,
    Clock24Regular,
} from '@fluentui/react-icons';

interface GeneratedNote {
    sections: {
        chiefComplaint: string;
        historyOfPresentIllness: string;
        mentalStatusExam: string;
        assessment: string;
        riskAssessment: {
            suicideRisk: 'Low' | 'Moderate' | 'High';
            violenceRisk: 'Low' | 'Moderate' | 'High';
            overallRisk: 'Low' | 'Moderate' | 'High';
        };
        plan: string;
        followUp: string[];
    };
    suggestedActions: string[];
    confidence: number;
    generatedAt: string;
    processingTime: number;
}

interface NoteReviewInterfaceProps {
    isOpen: boolean;
    consultationId: string;
    patientName: string;
    consultationType: string;
    transcription: string;
    generatedNote: GeneratedNote;
    onSave: (editedNote: GeneratedNote) => void;
    onSubmit: (finalNote: GeneratedNote) => void;
    onCancel: () => void;
    loading?: boolean;
}

export const NoteReviewInterface: React.FC<NoteReviewInterfaceProps> = ({
    isOpen,
    consultationId,
    patientName,
    consultationType,
    transcription,
    generatedNote,
    onSave,
    onSubmit,
    onCancel,
    loading = false,
}) => {
    const [activeTab, setActiveTab] = useState<string>('review');
    const [editedNote, setEditedNote] = useState<GeneratedNote>(generatedNote);
    const [hasChanges, setHasChanges] = useState(false);
    const [savingProgress, setSavingProgress] = useState(0);
    const { dispatchToast } = useToastController();

    useEffect(() => {
        setEditedNote(generatedNote);
    }, [generatedNote]);

    const handleSectionEdit = (sectionKey: keyof GeneratedNote['sections'], value: string) => {
        setEditedNote((prev) => ({
            ...prev,
            sections: {
                ...prev.sections,
                [sectionKey]: value,
            },
        }));
        setHasChanges(true);
    };

    const handleSave = async () => {
        setSavingProgress(0);
        const interval = setInterval(() => {
            setSavingProgress((prev) => {
                if (prev >= 100) {
                    clearInterval(interval);
                    return 100;
                }
                return prev + 20;
            });
        }, 100);

        await onSave(editedNote);
        setHasChanges(false);

        dispatchToast(
            <Toast>
                <ToastTitle>Draft Saved</ToastTitle>
            </Toast>,
            { intent: 'success' }
        );
    };

    const handleSubmit = async () => {
        await onSubmit(editedNote);
        dispatchToast(
            <Toast>
                <ToastTitle>Note Submitted to Epic EHR</ToastTitle>
            </Toast>,
            { intent: 'success' }
        );
    };

    const getRiskBadge = (riskLevel: string) => {
        switch (riskLevel) {
            case 'High':
                return (
                    <Badge appearance="filled" color="danger">
                        High Risk
                    </Badge>
                );
            case 'Moderate':
                return (
                    <Badge appearance="tint" color="warning">
                        Moderate Risk
                    </Badge>
                );
            case 'Low':
                return (
                    <Badge appearance="outline" color="success">
                        Low Risk
                    </Badge>
                );
            default:
                return <Badge appearance="outline">Unknown</Badge>;
        }
    };

    const getConfidenceColor = (confidence: number) => {
        if (confidence >= 85) return '#107c10';
        if (confidence >= 70) return '#ffa500';
        return '#d13438';
    };

    const renderReviewTab = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Generation Info */}
            <Card style={{ padding: '16px', backgroundColor: '#f0f8ff' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Bot24Regular style={{ fontSize: '24px', color: '#0078d4' }} />
                    <div style={{ flex: 1 }}>
                        <Text size={400} weight="semibold">
                            AI-Generated Clinical Note
                        </Text>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '16px',
                                marginTop: '4px',
                            }}
                        >
                            <Text size={300} style={{ color: '#605e5c' }}>
                                Confidence: {editedNote.confidence}%
                            </Text>
                            <Badge
                                appearance="tint"
                                color={
                                    editedNote.confidence >= 85
                                        ? 'success'
                                        : editedNote.confidence >= 70
                                        ? 'warning'
                                        : 'danger'
                                }
                            >
                                {editedNote.confidence >= 85
                                    ? 'High Confidence'
                                    : editedNote.confidence >= 70
                                    ? 'Moderate Confidence'
                                    : 'Low Confidence'}
                            </Badge>
                            <Text size={300} style={{ color: '#605e5c' }}>
                                Generated in {editedNote.processingTime}s
                            </Text>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Risk Assessment Summary */}
            <Card style={{ padding: '16px' }}>
                <Text size={500} weight="semibold" style={{ marginBottom: '12px' }}>
                    Risk Assessment Summary
                </Text>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '12px',
                    }}
                >
                    <div>
                        <Text size={300} style={{ color: '#605e5c' }}>
                            Suicide Risk
                        </Text>
                        {getRiskBadge(editedNote.sections.riskAssessment.suicideRisk)}
                    </div>
                    <div>
                        <Text size={300} style={{ color: '#605e5c' }}>
                            Violence Risk
                        </Text>
                        {getRiskBadge(editedNote.sections.riskAssessment.violenceRisk)}
                    </div>
                    <div>
                        <Text size={300} style={{ color: '#605e5c' }}>
                            Overall Risk
                        </Text>
                        {getRiskBadge(editedNote.sections.riskAssessment.overallRisk)}
                    </div>
                </div>
            </Card>

            {/* Suggested Actions */}
            {editedNote.suggestedActions.length > 0 && (
                <Card style={{ padding: '16px' }}>
                    <Text size={500} weight="semibold" style={{ marginBottom: '12px' }}>
                        AI-Suggested Actions
                    </Text>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {editedNote.suggestedActions.map((action, index) => (
                            <div
                                key={index}
                                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                            >
                                <CheckmarkCircle24Regular
                                    style={{ color: '#107c10', fontSize: '16px' }}
                                />
                                <Text size={300}>{action}</Text>
                            </div>
                        ))}
                    </div>
                </Card>
            )}
        </div>
    );

    const renderEditTab = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Chief Complaint */}
            <Card style={{ padding: '16px' }}>
                <Field label="Chief Complaint">
                    <Textarea
                        value={editedNote.sections.chiefComplaint}
                        onChange={(_, data) => handleSectionEdit('chiefComplaint', data.value)}
                        rows={3}
                        placeholder="Patient's primary complaint..."
                    />
                </Field>
            </Card>

            {/* History of Present Illness */}
            <Card style={{ padding: '16px' }}>
                <Field label="History of Present Illness">
                    <Textarea
                        value={editedNote.sections.historyOfPresentIllness}
                        onChange={(_, data) =>
                            handleSectionEdit('historyOfPresentIllness', data.value)
                        }
                        rows={6}
                        placeholder="Detailed history of current symptoms..."
                    />
                </Field>
            </Card>

            {/* Mental Status Examination */}
            <Card style={{ padding: '16px' }}>
                <Field label="Mental Status Examination">
                    <Textarea
                        value={editedNote.sections.mentalStatusExam}
                        onChange={(_, data) => handleSectionEdit('mentalStatusExam', data.value)}
                        rows={5}
                        placeholder="Appearance, behavior, speech, mood, affect, thought process..."
                    />
                </Field>
            </Card>

            {/* Assessment */}
            <Card style={{ padding: '16px' }}>
                <Field label="Assessment & Clinical Impression">
                    <Textarea
                        value={editedNote.sections.assessment}
                        onChange={(_, data) => handleSectionEdit('assessment', data.value)}
                        rows={4}
                        placeholder="Clinical assessment and diagnostic impressions..."
                    />
                </Field>
            </Card>

            {/* Plan */}
            <Card style={{ padding: '16px' }}>
                <Field label="Plan & Recommendations">
                    <Textarea
                        value={editedNote.sections.plan}
                        onChange={(_, data) => handleSectionEdit('plan', data.value)}
                        rows={5}
                        placeholder="Treatment plan, medications, interventions..."
                    />
                </Field>
            </Card>

            {/* Follow-up */}
            <Card style={{ padding: '16px' }}>
                <Field label="Follow-up Instructions">
                    <Textarea
                        value={editedNote.sections.followUp.join('\n')}
                        onChange={(_, data) => {
                            const followUpArray = data.value
                                .split('\n')
                                .filter((line) => line.trim());
                            setEditedNote((prev) => ({
                                ...prev,
                                sections: {
                                    ...prev.sections,
                                    followUp: followUpArray,
                                },
                            }));
                            setHasChanges(true);
                        }}
                        rows={3}
                        placeholder="Follow-up appointments, monitoring instructions..."
                    />
                </Field>
            </Card>
        </div>
    );

    const renderTranscriptionTab = () => (
        <Card style={{ padding: '16px' }}>
            <Text size={500} weight="semibold" style={{ marginBottom: '12px' }}>
                Original Transcription
            </Text>
            <div
                style={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    backgroundColor: '#f8f9fa',
                    padding: '16px',
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    lineHeight: '1.5',
                }}
            >
                {transcription}
            </div>
        </Card>
    );

    return (
        <Dialog open={isOpen}>
            <DialogSurface style={{ maxWidth: '900px', width: '95vw', height: '90vh' }}>
                <DialogBody>
                    <DialogTitle
                        action={
                            <Button
                                appearance="subtle"
                                aria-label="close"
                                icon={<Dismiss24Regular />}
                                onClick={onCancel}
                            />
                        }
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Document24Regular style={{ fontSize: '24px', color: '#0078d4' }} />
                            Clinical Note Review - {patientName}
                        </div>
                    </DialogTitle>

                    <DialogContent>
                        <div style={{ marginBottom: '16px' }}>
                            <Text size={400} style={{ color: '#605e5c' }}>
                                {consultationType} | Consultation ID: {consultationId}
                            </Text>
                        </div>

                        {savingProgress > 0 && savingProgress < 100 && (
                            <ProgressBar
                                value={savingProgress / 100}
                                style={{ marginBottom: '16px' }}
                            />
                        )}

                        <TabList
                            selectedValue={activeTab}
                            onTabSelect={(_, data) => setActiveTab(data.value as string)}
                            style={{ marginBottom: '20px' }}
                        >
                            <Tab value="review" icon={<Bot24Regular />}>
                                AI Review
                            </Tab>
                            <Tab value="edit" icon={<Edit24Regular />}>
                                Edit Note
                                {hasChanges && (
                                    <Badge
                                        size="small"
                                        color="warning"
                                        style={{ marginLeft: '8px' }}
                                    >
                                        *
                                    </Badge>
                                )}
                            </Tab>
                            <Tab value="transcription" icon={<Person24Regular />}>
                                Transcription
                            </Tab>
                        </TabList>

                        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                            {activeTab === 'review' && renderReviewTab()}
                            {activeTab === 'edit' && renderEditTab()}
                            {activeTab === 'transcription' && renderTranscriptionTab()}
                        </div>
                    </DialogContent>

                    <DialogActions>
                        <Button appearance="secondary" onClick={onCancel}>
                            Cancel
                        </Button>

                        {hasChanges && (
                            <Button
                                appearance="outline"
                                icon={<Save24Regular />}
                                onClick={handleSave}
                                disabled={loading}
                            >
                                Save Draft
                            </Button>
                        )}

                        <Button
                            appearance="primary"
                            icon={<Send24Regular />}
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? 'Submitting...' : 'Submit to Epic EHR'}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
            <Toaster />
        </Dialog>
    );
};
